import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../errors';
import { Button, Dialog, TextField } from '../../ui';

export const VIEW_NAME_MAX = 80;

/**
 * Names a view: saving the list as a new view, or renaming one. `onSubmit(name)`
 * resolves once saved (the dialog then closes) or rejects with the API's error,
 * shown under the field — a twin name (view_exists), the limit (view_limit).
 * `summary`: what is being saved, read out under the title.
 */
export function ViewNameDialog({ open, title, initialName = '', submitLabel, summary, onSubmit, onClose }) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setName(initialName);
      setError(null);
      setSaving(false);
    }
  }, [open, initialName]);

  const trimmed = name.trim();
  const submit = async (event) => {
    event.preventDefault();
    if (!trimmed) {
      setError(t('views.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(trimmed);
    } catch (failure) {
      const code = failure && failure.response && failure.response.data && failure.response.data.code;
      setError(code === 'view_exists' ? t('views.nameTaken', { name: trimmed }) : errorMessage(t, failure));
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={summary}
      size="sm"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="saved-view-name" loading={saving}>{submitLabel}</Button>
        </>
      )}
    >
      <form id="saved-view-name" className="dialog-form" onSubmit={submit} noValidate>
        <TextField
          label={t('views.nameLabel')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={VIEW_NAME_MAX}
          hint={t('views.nameHint', { max: VIEW_NAME_MAX })}
          error={error}
          autoComplete="off"
          required
        />
      </form>
    </Dialog>
  );
}

export default ViewNameDialog;
